import * as yup from 'yup';
import { optionalAmount, optionalCount } from 'pages/PosSettings/numberRules';
import { MarketingTypes } from 'components/Coupons/types';

/** Marketing campaigns pick from the coupons a merchant created by hand. */
const MANUAL_CREATION_TYPE = 'manual';

// PERSONAL, PARTNER and REFERRAL are hidden until a customer selector exists:
// PERSONAL/REFERRAL reject anonymous orders but cannot be bound to a customer
// without assignedCustomerId, and PARTNER attribution is never read by the SDK.
export const COUPON_TYPE_OPTIONS = [
  { value: 'STATIC', label: 'Static' },
  { value: 'STAFF', label: 'Staff' },
];

export const couponDefaultValues = {
  couponCode: '',
  couponType: 'STATIC',
  maxUses: null,
  maxUsesPerCustomer: null,
  maxUsesPerOrder: null,
  maxUsesPerStore: null,
  maxTotalDiscount: null,
};

const toNumberOrNull = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

// A blank limit means unlimited; a filled one has to be a countable number.
const usageCount = () =>
  optionalCount({ min: 1, minMessage: 'this_field_equal_to_1' });

// An empty code has to pass these so a promotion that needs no coupon stays
// valid; `required` is what enforces presence when a code is actually needed.
const couponCodeRules = yup
  .string()
  .test('len', 'requires_min_4_character', (val) => !val || val.length >= 4)
  .matches(/^[a-zA-Z0-9]+$/, {
    message: 'only_letters',
    excludeEmptyString: true,
  });

/**
 * The coupon page always needs a code. The promotion form only needs one once
 * the promotion is set to require a coupon, so there it hangs off `trigger`.
 */
export const couponSchemaFields = ({ requiredWithTrigger = false } = {}) => ({
  couponCode: requiredWithTrigger
    ? couponCodeRules.when('trigger', {
        is: 'CODE',
        then: couponCodeRules.required('field_is_required_'),
      })
    : couponCodeRules.required('field_is_required_'),
  couponType: yup.string().nullable(),
  useForMarketing: yup.bool(),
  maxUses: usageCount(),
  maxUsesPerCustomer: usageCount(),
  maxUsesPerOrder: usageCount(),
  maxUsesPerStore: usageCount(),
  maxTotalDiscount: optionalAmount({
    min: 0,
    minMessage: 'this_must_be_greater_0',
  }),
});

export const couponToFormValues = (coupon) => ({
  couponCode: coupon?.code || '',
  couponType: coupon?.type || 'STATIC',
  maxUses: coupon?.limits?.maxUses ?? null,
  maxUsesPerCustomer: coupon?.limits?.maxUsesPerCustomer ?? null,
  maxUsesPerOrder: coupon?.limits?.maxUsesPerOrder ?? null,
  maxUsesPerStore: coupon?.limits?.maxUsesPerStore ?? null,
  maxTotalDiscount: coupon?.limits?.maxTotalDiscount ?? null,
});

/**
 * Whether the coupon is offered to marketing campaigns. A new one is, as the
 * website coupon form has always offered it; an existing one keeps what it was
 * saved with.
 */
export const couponMarketingValue = (coupon) =>
  coupon ? coupon.legacyType === MarketingTypes.MARKETING : true;

/**
 * The coupon is sent inside its promotion, so it carries no business or
 * promotion of its own: the service takes both from the promotion it belongs to
 * and writes the pair together.
 *
 * `legacy*` is what marketing lists its coupons by — the business as a mongo
 * id, the category, and how the coupon was created — so a coupon meant for a
 * campaign has to be written with all three. Only a form that asks for the
 * choice sends them; the promotion form leaves them as they are rather than
 * overwriting a choice it never showed.
 */
export const buildCouponPayload = ({
  values,
  startAt,
  endAt,
  active,
  businessId,
}) => ({
  code: (values.couponCode || '').trim(),
  type: values.couponType || 'STATIC',
  status: active ? 'ACTIVE' : 'INACTIVE',
  ...(values.useForMarketing === undefined
    ? {}
    : {
        legacyBusinessIds: businessId ? [businessId] : [],
        legacyType: values.useForMarketing
          ? MarketingTypes.MARKETING
          : MarketingTypes.SYSTEM,
        legacyCreationType: MANUAL_CREATION_TYPE,
      }),
  startAt,
  endAt,
  limits: {
    maxUses: toNumberOrNull(values.maxUses),
    maxUsesPerCustomer: toNumberOrNull(values.maxUsesPerCustomer),
    maxUsesPerOrder: toNumberOrNull(values.maxUsesPerOrder),
    maxUsesPerStore: toNumberOrNull(values.maxUsesPerStore),
    maxTotalDiscount: toNumberOrNull(values.maxTotalDiscount),
  },
});
